import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const API_BASE    = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_STOREFRONT_API_URL) || 'https://dashboard.offloadhost.com';
const CUSTOMER_ID = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CUSTOMER_ID) || '';

// State shape: 'verifying' | 'success' | 'expired' | 'invalid' | 'no_token' | 'network_error'
const VerifyEmail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('verifying');

  useEffect(() => {
    const token = params.get('token');
    if (!token) { setState('no_token'); return; }
    const ctl = new AbortController();
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ token }),
          signal: ctl.signal,
        });
        if (r.ok) { setState('success'); return; }
        let data = null; try { data = await r.json(); } catch (_) {}
        const msg = (data?.error || '').toLowerCase();
        if (msg.includes('expired'))      setState('expired');
        else                              setState('invalid');
      } catch (err) {
        if (err?.name !== 'AbortError') setState('network_error');
      }
    })();
    return () => ctl.abort();
  }, [params]);

  const heading = {
    verifying:     'Verifying your email…',
    success:       'Email verified',
    expired:       'This link expired',
    invalid:       'This verification link is invalid',
    no_token:      'Missing verification token',
    network_error: 'Network error — please try again',
  }[state];

  const body = {
    verifying:     'Hang on while we confirm your account.',
    success:       'You can now sign in to your Bounderlaster account.',
    expired:       'Verification links are good for 24 hours. Request a new one from the sign-in page.',
    invalid:       'Double-check the link in your email — or request a new one from the sign-in page.',
    no_token:      'Open the verification email and click the link inside.',
    network_error: 'Something went wrong on our side. Try refreshing the page.',
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md text-center bg-card border border-border rounded-2xl shadow-elevation-2 p-8 space-y-4">
        <div className="flex justify-center">
          {state === 'verifying' && <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />}
          {state === 'success'   && <Icon name="CheckCircle" size={48} className="text-primary" />}
          {(state === 'expired' || state === 'invalid' || state === 'no_token') && <Icon name="AlertCircle" size={48} className="text-destructive" />}
          {state === 'network_error' && <Icon name="WifiOff" size={48} className="text-destructive" />}
        </div>
        <h1 className="text-2xl font-heading font-semibold text-foreground">{heading}</h1>
        <p className="text-muted-foreground">{body}</p>
        {state === 'success' && (
          <Button onClick={() => navigate('/user-login')} fullWidth>Go to sign-in</Button>
        )}
        {(state === 'expired' || state === 'invalid' || state === 'no_token') && (
          <Link to="/user-login" className="text-sm text-primary hover:underline">Back to sign-in</Link>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
